import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardActions,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  IconButton,
  Paper,
  TextField,
  Typography,
  Alert,
  Snackbar,
  Switch,
  FormControlLabel,
  Divider,
  Chip,
  Tooltip,
  Skeleton,
  Avatar,
  InputAdornment,
  Badge,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon,
  Label as LabelIcon,
  Category as CategoryIcon,
  Search as SearchIcon,
  Warning as WarningIcon,
  Refresh as RefreshIcon,
  DragIndicator as DragIcon,
  Circle as CircleIcon,
  Palette as PaletteIcon,
  Inventory as AssetIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import './CategoryTagManagementPage.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080';

const PRESET_COLORS = [
  '#f44336', '#e91e63', '#9c27b0', '#673ab7', '#3f51b5',
  '#2196f3', '#03a9f4', '#00bcd4', '#009688', '#4caf50',
  '#8bc34a', '#cddc39', '#ffc107', '#ff9800', '#ff5722',
  '#795548', '#607d8b', '#1976d2',
];

const CategoryTagManagementPage = () => {
  const { getAccessToken } = useAuth();
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [tagModalOpen, setTagModalOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedTag, setSelectedTag] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [categorySearch, setCategorySearch] = useState('');
  const [tagSearch, setTagSearch] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, item: null, type: null });

  const [categoryForm, setCategoryForm] = useState({
    name: '',
    description: '',
    displayOrder: 0,
    isActive: true
  });

  const [tagForm, setTagForm] = useState({
    name: '',
    color: '#1976d2'
  });

  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  useEffect(() => {
    fetchCategories();
    fetchTags();
  }, []);

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const token = await getAccessToken();
      if (!token) { showNotification('Authentication required', 'error'); return; }
      const response = await axios.get(`${API_BASE_URL}/api/v1/content/categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCategories(response.data.sort((a, b) => a.displayOrder - b.displayOrder));
    } catch (error) {
      showNotification('Failed to fetch categories', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchTags = async () => {
    try {
      const token = await getAccessToken();
      if (!token) { showNotification('Authentication required', 'error'); return; }
      const response = await axios.get(`${API_BASE_URL}/api/v1/content/tags`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTags(response.data);
    } catch (error) {
      showNotification('Failed to fetch tags', 'error');
    }
  };

  // Category handlers
  const handleCreateCategory = () => {
    setIsEditMode(false);
    setSelectedCategory(null);
    setCategoryForm({ name: '', description: '', displayOrder: categories.length, isActive: true });
    setCategoryModalOpen(true);
  };

  const handleEditCategory = (category) => {
    setIsEditMode(true);
    setSelectedCategory(category);
    setCategoryForm({ name: category.name, description: category.description || '', displayOrder: category.displayOrder, isActive: category.isActive });
    setCategoryModalOpen(true);
  };

  const handleSaveCategory = async () => {
    if (!categoryForm.name) { showNotification('Please enter a category name', 'error'); return; }
    try {
      const token = await getAccessToken();
      if (!token) { showNotification('Authentication required', 'error'); return; }
      if (isEditMode && selectedCategory) {
        await axios.put(`${API_BASE_URL}/api/v1/content/categories/${selectedCategory.id}`, categoryForm, { headers: { Authorization: `Bearer ${token}` } });
        showNotification('Category updated successfully', 'success');
      } else {
        await axios.post(`${API_BASE_URL}/api/v1/content/categories`, categoryForm, { headers: { Authorization: `Bearer ${token}` } });
        showNotification('Category created successfully', 'success');
      }
      setCategoryModalOpen(false);
      fetchCategories();
    } catch (error) {
      handleApiError(error, isEditMode ? 'Update failed' : 'Create failed');
    }
  };

  const handleDeleteConfirmed = async () => {
    const { item, type } = deleteConfirm;
    if (!item) return;
    try {
      const token = await getAccessToken();
      if (!token) { showNotification('Authentication required', 'error'); return; }
      const endpoint = type === 'category' ? 'categories' : 'tags';
      await axios.delete(`${API_BASE_URL}/api/v1/content/${endpoint}/${item.id}`, { headers: { Authorization: `Bearer ${token}` } });
      showNotification(`${type === 'category' ? 'Category' : 'Tag'} deleted successfully`, 'success');
      setDeleteConfirm({ open: false, item: null, type: null });
      if (type === 'category') fetchCategories(); else fetchTags();
    } catch (error) {
      handleApiError(error, 'Delete failed');
      setDeleteConfirm({ open: false, item: null, type: null });
    }
  };

  const toCategoryPayload = (cat) => ({
    name: cat.name,
    description: cat.description || '',
    displayOrder: cat.displayOrder,
    isActive: cat.isActive,
  });

  const handleToggleCategoryActive = async (category) => {
    try {
      const token = await getAccessToken();
      if (!token) { showNotification('Authentication required', 'error'); return; }
      await axios.put(`${API_BASE_URL}/api/v1/content/categories/${category.id}`, { ...toCategoryPayload(category), isActive: !category.isActive }, { headers: { Authorization: `Bearer ${token}` } });
      showNotification(`Category ${!category.isActive ? 'activated' : 'deactivated'}`, 'success');
      fetchCategories();
    } catch (error) {
      handleApiError(error, 'Status update failed');
    }
  };

  const handleMoveCategoryUp = async (category, index) => {
    if (index === 0) return;
    const prev = categories[index - 1];
    try {
      const token = await getAccessToken();
      if (!token) return;
      await axios.put(`${API_BASE_URL}/api/v1/content/categories/${category.id}`, { ...toCategoryPayload(category), displayOrder: prev.displayOrder }, { headers: { Authorization: `Bearer ${token}` } });
      await axios.put(`${API_BASE_URL}/api/v1/content/categories/${prev.id}`, { ...toCategoryPayload(prev), displayOrder: category.displayOrder }, { headers: { Authorization: `Bearer ${token}` } });
      fetchCategories();
    } catch (error) {
      handleApiError(error, 'Failed to reorder');
    }
  };

  const handleMoveCategoryDown = async (category, index) => {
    if (index === categories.length - 1) return;
    const next = categories[index + 1];
    try {
      const token = await getAccessToken();
      if (!token) return;
      await axios.put(`${API_BASE_URL}/api/v1/content/categories/${category.id}`, { ...toCategoryPayload(category), displayOrder: next.displayOrder }, { headers: { Authorization: `Bearer ${token}` } });
      await axios.put(`${API_BASE_URL}/api/v1/content/categories/${next.id}`, { ...toCategoryPayload(next), displayOrder: category.displayOrder }, { headers: { Authorization: `Bearer ${token}` } });
      fetchCategories();
    } catch (error) {
      handleApiError(error, 'Failed to reorder');
    }
  };

  // Tag handlers
  const handleCreateTag = () => {
    setIsEditMode(false);
    setSelectedTag(null);
    setTagForm({ name: '', color: '#1976d2' });
    setTagModalOpen(true);
  };

  const handleEditTag = (tag) => {
    setIsEditMode(true);
    setSelectedTag(tag);
    setTagForm({ name: tag.name, color: tag.color || '#1976d2' });
    setTagModalOpen(true);
  };

  const handleSaveTag = async () => {
    if (!tagForm.name) { showNotification('Please enter a tag name', 'error'); return; }
    if (!/^#[0-9A-F]{6}$/i.test(tagForm.color)) { showNotification('Please enter a valid hex color', 'error'); return; }
    try {
      const token = await getAccessToken();
      if (!token) { showNotification('Authentication required', 'error'); return; }
      if (isEditMode && selectedTag) {
        await axios.put(`${API_BASE_URL}/api/v1/content/tags/${selectedTag.id}`, tagForm, { headers: { Authorization: `Bearer ${token}` } });
        showNotification('Tag updated successfully', 'success');
      } else {
        await axios.post(`${API_BASE_URL}/api/v1/content/tags`, tagForm, { headers: { Authorization: `Bearer ${token}` } });
        showNotification('Tag created successfully', 'success');
      }
      setTagModalOpen(false);
      fetchTags();
    } catch (error) {
      handleApiError(error, isEditMode ? 'Update failed' : 'Create failed');
    }
  };

  const showNotification = (message, severity = 'success') => {
    setNotification({ open: true, message, severity });
  };

  const handleApiError = (error, defaultMessage) => {
    if (error.response?.status === 401) showNotification('Session expired. Please login again.', 'error');
    else if (error.response?.status === 403) showNotification('No permission for this action.', 'error');
    else if (error.response?.data?.message) showNotification(error.response.data.message, 'error');
    else showNotification(defaultMessage, 'error');
  };

  const filteredCategories = categories.filter(c =>
    !categorySearch || c.name.toLowerCase().includes(categorySearch.toLowerCase())
  );
  const filteredTags = tags.filter(t =>
    !tagSearch || t.name.toLowerCase().includes(tagSearch.toLowerCase())
  );

  const catStats = { total: categories.length, active: categories.filter(c => c.isActive).length, totalAssets: categories.reduce((s, c) => s + (c.assetCount || 0), 0) };
  const tagStats = { total: tags.length, totalAssets: tags.reduce((s, t) => s + (t.assetCount || 0), 0) };

  return (
    <Box className="category-tag-management-page">
      {/* Header */}
      <Box className="page-header">
        <Box>
          <Typography variant="h4" fontWeight={700}>Categories & Tags</Typography>
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            Organize your content with categories and tags
          </Typography>
        </Box>
        <Box display="flex" gap={1}>
          <Tooltip title="Refresh all">
            <IconButton onClick={() => { fetchCategories(); fetchTags(); }} color="primary">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Stats Row */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Categories', value: catStats.total, icon: <CategoryIcon />, color: '#667eea' },
          { label: 'Active Categories', value: catStats.active, icon: <CircleIcon />, color: '#4caf50' },
          { label: 'Tags', value: tagStats.total, icon: <LabelIcon />, color: '#ff9800' },
          { label: 'Total Tagged Assets', value: tagStats.totalAssets, icon: <AssetIcon />, color: '#e91e63' },
        ].map((stat, i) => (
          <Grid item xs={6} md={3} key={i}>
            <Paper sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2, borderRadius: 3, border: '1px solid', borderColor: 'divider', transition: 'all 0.2s', '&:hover': { transform: 'translateY(-2px)', boxShadow: 3 } }}>
              <Avatar sx={{ bgcolor: stat.color + '20', color: stat.color, width: 44, height: 44 }}>
                {stat.icon}
              </Avatar>
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={500}>{stat.label}</Typography>
                <Typography variant="h5" fontWeight={700}>{stat.value}</Typography>
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Split View */}
      <Grid container spacing={3}>
        {/* Categories Section */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', overflow: 'hidden', display: 'flex', flexDirection: 'column', maxHeight: { md: 'calc(100vh - 340px)' } }}>
            <Box sx={{ p: 2.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: 'background.default' }}>
              <Box display="flex" alignItems="center" gap={1}>
                <Avatar sx={{ bgcolor: '#667eea20', color: '#667eea', width: 36, height: 36 }}><CategoryIcon fontSize="small" /></Avatar>
                <Box>
                  <Typography variant="subtitle1" fontWeight={700}>Categories</Typography>
                  <Typography variant="caption" color="text.secondary">{categories.length} total</Typography>
                </Box>
              </Box>
              <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={handleCreateCategory} sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}>
                Add
              </Button>
            </Box>

            <Divider />

            <Box sx={{ px: 2, py: 1.5 }}>
              <TextField
                fullWidth size="small" placeholder="Search categories..."
                value={categorySearch} onChange={e => setCategorySearch(e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" color="action" /></InputAdornment> }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Box>

            <Box sx={{ flex: 1, overflow: 'auto', px: 2, pb: 2 }}>
              {loading ? (
                [1, 2, 3].map(i => <Skeleton key={i} variant="rounded" height={100} sx={{ mb: 1.5, borderRadius: 2 }} />)
              ) : filteredCategories.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                  <CategoryIcon sx={{ fontSize: 56, color: 'text.disabled', mb: 1 }} />
                  <Typography color="text.secondary" fontWeight={500}>
                    {categorySearch ? 'No matching categories' : 'No categories yet'}
                  </Typography>
                  {!categorySearch && (
                    <Button size="small" startIcon={<AddIcon />} onClick={handleCreateCategory} sx={{ mt: 1, textTransform: 'none' }}>
                      Create first category
                    </Button>
                  )}
                </Box>
              ) : (
                filteredCategories.map((category, index) => (
                  <Card key={category.id} sx={{ mb: 1.5, borderRadius: 2, border: '1px solid', borderColor: 'divider', transition: 'all 0.2s', opacity: category.isActive ? 1 : 0.65, '&:hover': { borderColor: 'primary.main', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' } }}>
                    <CardContent sx={{ p: '12px 16px !important', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      {/* Order indicator */}
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 32 }}>
                        <Tooltip title="Move up">
                          <span>
                            <IconButton size="small" onClick={() => handleMoveCategoryUp(category, index)} disabled={index === 0} sx={{ p: '2px' }}>
                              <ArrowUpIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Typography variant="caption" fontWeight={700} color="text.disabled">{index + 1}</Typography>
                        <Tooltip title="Move down">
                          <span>
                            <IconButton size="small" onClick={() => handleMoveCategoryDown(category, index)} disabled={index === categories.length - 1} sx={{ p: '2px' }}>
                              <ArrowDownIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>

                      {/* Content */}
                      <Box flex={1} minWidth={0}>
                        <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                          <Typography variant="subtitle2" fontWeight={700} noWrap>{category.name}</Typography>
                          <Chip
                            label={category.isActive ? 'Active' : 'Inactive'}
                            size="small"
                            sx={{ height: 20, fontSize: '0.65rem', fontWeight: 600, bgcolor: category.isActive ? '#e8f5e9' : '#fafafa', color: category.isActive ? '#2e7d32' : '#9e9e9e' }}
                          />
                        </Box>
                        <Typography variant="caption" color="text.secondary" noWrap display="block">
                          {category.description || 'No description'}
                        </Typography>
                        <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                          <Chip icon={<AssetIcon sx={{ fontSize: '14px !important' }} />} label={`${category.assetCount || 0} assets`} size="small" variant="outlined" sx={{ height: 22, fontSize: '0.65rem' }} />
                        </Box>
                      </Box>

                      {/* Actions */}
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <Tooltip title={category.isActive ? 'Deactivate' : 'Activate'}>
                          <Switch size="small" checked={category.isActive} onChange={() => handleToggleCategoryActive(category)} color="success" />
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => handleEditCategory(category)} color="primary"><EditIcon fontSize="small" /></IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" onClick={() => setDeleteConfirm({ open: true, item: category, type: 'category' })} color="error"><DeleteIcon fontSize="small" /></IconButton>
                        </Tooltip>
                      </Box>
                    </CardContent>
                  </Card>
                ))
              )}
            </Box>
          </Paper>
        </Grid>

        {/* Tags Section */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', overflow: 'hidden', display: 'flex', flexDirection: 'column', maxHeight: { md: 'calc(100vh - 340px)' } }}>
            <Box sx={{ p: 2.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: 'background.default' }}>
              <Box display="flex" alignItems="center" gap={1}>
                <Avatar sx={{ bgcolor: '#ff980020', color: '#ff9800', width: 36, height: 36 }}><LabelIcon fontSize="small" /></Avatar>
                <Box>
                  <Typography variant="subtitle1" fontWeight={700}>Tags</Typography>
                  <Typography variant="caption" color="text.secondary">{tags.length} total</Typography>
                </Box>
              </Box>
              <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={handleCreateTag} sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}>
                Add
              </Button>
            </Box>

            <Divider />

            <Box sx={{ px: 2, py: 1.5 }}>
              <TextField
                fullWidth size="small" placeholder="Search tags..."
                value={tagSearch} onChange={e => setTagSearch(e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" color="action" /></InputAdornment> }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Box>

            <Box sx={{ flex: 1, overflow: 'auto', px: 2, pb: 2 }}>
              {tags.length === 0 && !loading ? (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                  <LabelIcon sx={{ fontSize: 56, color: 'text.disabled', mb: 1 }} />
                  <Typography color="text.secondary" fontWeight={500}>No tags yet</Typography>
                  <Button size="small" startIcon={<AddIcon />} onClick={handleCreateTag} sx={{ mt: 1, textTransform: 'none' }}>
                    Create first tag
                  </Button>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                  {filteredTags.map((tag) => (
                    <Card key={tag.id} sx={{ borderRadius: 3, border: '2px solid', borderColor: tag.color + '40', transition: 'all 0.2s', minWidth: 180, flex: '1 1 calc(50% - 12px)', maxWidth: 'calc(50% - 6px)', '&:hover': { borderColor: tag.color, boxShadow: `0 4px 16px ${tag.color}30`, transform: 'translateY(-2px)' } }}>
                      <CardContent sx={{ p: '14px !important' }}>
                        <Box display="flex" alignItems="center" gap={1.5} mb={1}>
                          <Box sx={{ width: 32, height: 32, borderRadius: '50%', bgcolor: tag.color, boxShadow: `0 2px 8px ${tag.color}60`, flexShrink: 0 }} />
                          <Box flex={1} minWidth={0}>
                            <Typography variant="subtitle2" fontWeight={700} noWrap>{tag.name}</Typography>
                            <Typography variant="caption" color="text.secondary">{tag.color}</Typography>
                          </Box>
                        </Box>
                        <Box display="flex" alignItems="center" justifyContent="space-between">
                          <Chip icon={<AssetIcon sx={{ fontSize: '14px !important' }} />} label={`${tag.assetCount || 0} assets`} size="small" variant="outlined" sx={{ height: 22, fontSize: '0.65rem' }} />
                          <Box display="flex" gap={0.5}>
                            <Tooltip title="Edit">
                              <IconButton size="small" onClick={() => handleEditTag(tag)} sx={{ color: tag.color }}><EditIcon sx={{ fontSize: 18 }} /></IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton size="small" onClick={() => setDeleteConfirm({ open: true, item: tag, type: 'tag' })} color="error"><DeleteIcon sx={{ fontSize: 18 }} /></IconButton>
                            </Tooltip>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
                  {filteredTags.length === 0 && tagSearch && (
                    <Box sx={{ textAlign: 'center', py: 4, width: '100%' }}>
                      <Typography color="text.secondary">No matching tags</Typography>
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Category Modal */}
      <Dialog open={categoryModalOpen} onClose={() => setCategoryModalOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, pb: 0.5 }}>
          {isEditMode ? 'Edit Category' : 'Create Category'}
          <Typography variant="body2" color="text.secondary">
            {isEditMode ? 'Update category details' : 'Add a new category to organize content'}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box mt={1.5}>
            <Grid container spacing={2.5}>
              <Grid item xs={12}>
                <TextField fullWidth label="Name *" value={categoryForm.name} onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Description" multiline rows={3} value={categoryForm.description} onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth label="Display Order" type="number" value={categoryForm.displayOrder} onChange={(e) => setCategoryForm({ ...categoryForm, displayOrder: parseInt(e.target.value) || 0 })} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
              </Grid>
              <Grid item xs={6}>
                <FormControlLabel
                  control={<Switch checked={categoryForm.isActive} onChange={(e) => setCategoryForm({ ...categoryForm, isActive: e.target.checked })} color="success" />}
                  label={<Typography variant="body2" fontWeight={500}>{categoryForm.isActive ? 'Active' : 'Inactive'}</Typography>}
                  sx={{ mt: 1 }}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, pt: 1 }}>
          <Button onClick={() => setCategoryModalOpen(false)} sx={{ borderRadius: 2, textTransform: 'none' }}>Cancel</Button>
          <Button onClick={handleSaveCategory} variant="contained" color="primary" sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, px: 4 }}>
            {isEditMode ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Tag Modal */}
      <Dialog open={tagModalOpen} onClose={() => setTagModalOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, pb: 0.5 }}>
          {isEditMode ? 'Edit Tag' : 'Create Tag'}
          <Typography variant="body2" color="text.secondary">
            {isEditMode ? 'Update tag details' : 'Add a new tag for content labeling'}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box mt={1.5}>
            <Grid container spacing={2.5}>
              <Grid item xs={12}>
                <TextField fullWidth label="Name *" value={tagForm.name} onChange={(e) => setTagForm({ ...tagForm, name: e.target.value })} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>Color</Typography>
                <Box display="flex" alignItems="center" gap={2} mb={2}>
                  <Box sx={{ width: 48, height: 48, borderRadius: 2, bgcolor: tagForm.color, boxShadow: `0 4px 12px ${tagForm.color}50`, border: '2px solid white', outline: '1px solid', outlineColor: 'divider' }} />
                  <TextField
                    value={tagForm.color}
                    onChange={(e) => setTagForm({ ...tagForm, color: e.target.value })}
                    placeholder="#1976d2" size="small"
                    InputProps={{ startAdornment: <InputAdornment position="start"><PaletteIcon fontSize="small" sx={{ color: tagForm.color }} /></InputAdornment> }}
                    sx={{ flex: 1, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Box>
                <Typography variant="caption" color="text.secondary" display="block" mb={1}>Quick pick a color:</Typography>
                <Box display="flex" flexWrap="wrap" gap={1}>
                  {PRESET_COLORS.map(color => (
                    <Tooltip key={color} title={color}>
                      <Box
                        onClick={() => setTagForm({ ...tagForm, color })}
                        sx={{
                          width: 32, height: 32, borderRadius: '50%', bgcolor: color, cursor: 'pointer',
                          border: tagForm.color === color ? '3px solid' : '2px solid transparent',
                          borderColor: tagForm.color === color ? 'text.primary' : 'transparent',
                          transition: 'all 0.2s', '&:hover': { transform: 'scale(1.2)', boxShadow: `0 2px 8px ${color}80` }
                        }}
                      />
                    </Tooltip>
                  ))}
                </Box>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, pt: 1 }}>
          <Button onClick={() => setTagModalOpen(false)} sx={{ borderRadius: 2, textTransform: 'none' }}>Cancel</Button>
          <Button onClick={handleSaveTag} variant="contained" sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, px: 4, bgcolor: tagForm.color, '&:hover': { bgcolor: tagForm.color, filter: 'brightness(0.9)' } }}>
            {isEditMode ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={deleteConfirm.open} onClose={() => setDeleteConfirm({ open: false, item: null, type: null })} PaperProps={{ sx: { borderRadius: 3, maxWidth: 440 } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon color="error" />
          Delete {deleteConfirm.type === 'category' ? 'Category' : 'Tag'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" mb={1}>
            Are you sure you want to delete <strong>"{deleteConfirm.item?.name}"</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {deleteConfirm.type === 'category'
              ? 'Assets in this category will not be deleted but will become uncategorized.'
              : 'This tag will be removed from all associated assets.'}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, pt: 1 }}>
          <Button onClick={() => setDeleteConfirm({ open: false, item: null, type: null })} sx={{ borderRadius: 2, textTransform: 'none' }}>Cancel</Button>
          <Button onClick={handleDeleteConfirmed} variant="contained" color="error" sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}>Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Notification */}
      <Snackbar open={notification.open} autoHideDuration={4000} onClose={() => setNotification({ ...notification, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert onClose={() => setNotification({ ...notification, open: false })} severity={notification.severity} variant="filled" sx={{ borderRadius: 2 }}>
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CategoryTagManagementPage;
